import React, { useState, useMemo } from 'react';
import { STAGES, STAGES_CONFIG } from '../constants/crmConstants';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../services/firebaseConfig';

const StudentProfile = ({ student, currentUser, settings, users, onClose, onAddTimelineEvent, onUpdateStage, onUpdateStudent, onUpdateDocuments, onEditProfile, viewOnly = false }) => {
  const [activeTab, setActiveTab] = useState('timeline'); // 'timeline', 'checklist', or 'documents'
  const [newNote, setNewNote] = useState('');
  const [showStageModal, setShowStageModal] = useState(false);
  const [stageForm, setStageForm] = useState({ stage: student.stage, reason: '' });
  const [docStatusFilter, setDocStatusFilter] = useState('All');
  
  // Local state for additional student docs
  const [customDocName, setCustomDocName] = useState('');

  // Simplified Status mapping
  const STATUS_OPTIONS = ['Pending', 'Complete'];

  const config = STAGES_CONFIG[student.stage] || { color: '#606161', icon: '👤' };

  // Helper to handle name inheritance if 'name' field is missing
  const displayName = student.name || `${student.firstName || ''} ${student.lastName || ''}`.trim() || 'Unnamed Student';

  const handleFileUpload = async (e) => {
    if (viewOnly) return;
    const file = e.target.files[0];
    if (!file) return;

    try {
        // Use a timestamp to prevent filename collisions in Storage
        const storagePath = `students/${student.id}/${Date.now()}_${file.name}`;
        const storageRef = ref(storage, storagePath);
        
        const snapshot = await uploadBytes(storageRef, file);
        const url = await getDownloadURL(snapshot.ref);

        const newDoc = {
            name: file.name,
            url: url,
            uploadedAt: new Date().toISOString()
        };

        // Create the updated array
        const currentDocs = Array.isArray(student.evaluationDocuments) ? student.evaluationDocuments : [];
        const updatedDocs = [...currentDocs, newDoc];

        // FIX: Ensure we await the update so the UI receives the new data
        await onUpdateStudent({ evaluationDocuments: updatedDocs }, student.id);
        
        // Add to timeline
        await onAddTimelineEvent(student.id, {
            type: 'system',
            content: `New document uploaded: ${file.name}`,
            timestamp: new Date().toISOString(),
            user: currentUser?.name || 'User'
        });

        // Clear the input so the same file can be uploaded again if needed
        e.target.value = '';
        
    } catch (error) {
        console.error("Upload failed:", error);
        alert("Upload failed. Please check your connection.");
    }
  };





  /**
   * Logic to find the B2B Team Owner associated with the student's Source Agent.
   * Handles both legacy string format and new object format in settings.
   */
  const b2bOwner = useMemo(() => {
    if (!student.sourceAgent || !settings.sourceAgents || !users) return null;

    // 1. Find the agent entry in settings. Link by name.
    const agentEntry = settings.sourceAgents.find(a => {
        // Handle both old string format and new object format
        const agentName = typeof a === 'string' ? a : a.name;
        return agentName === student.sourceAgent;
    });

    // 2. If that agent entry is an object and has a linked userId, find that user in directory
    if (agentEntry && typeof agentEntry === 'object' && agentEntry.userId) {
        return users.find(u => String(u.uid) === String(agentEntry.userId));
    }
    return null;
  }, [student.sourceAgent, settings.sourceAgents, users]);

  // Sync Documents Logic
  const checklistItems = useMemo(() => {
    // Priority: Specific University template -> Target Country template -> Empty array
    const baseTemplate = settings.documentTemplates?.[student.destinationUniversity] || settings.documentTemplates?.[student.targetCountry] || [];
    const studentDocs = student.documentChecklist || {};
    
    let merged = baseTemplate.map(name => ({
        id: `template-${name}`,
        name: name,
        status: studentDocs[name]?.status || 'Pending',
        notes: studentDocs[name]?.notes || '',
        shared: studentDocs[name]?.shared || false,
        isTemplate: true
    }));

    Object.entries(studentDocs).forEach(([name, data]) => {
        if (!baseTemplate.includes(name)) {
            merged.push({
                id: `custom-${name}`,
                name: name,
                status: data.status || 'Pending',
                notes: data.notes || '',
                shared: data.shared || false,
                isTemplate: false
            });
        }
    });

    if (docStatusFilter !== 'All') {
        merged = merged.filter(item => item.status === docStatusFilter);
    }

    return merged;
  }, [student.destinationUniversity, student.targetCountry, student.documentChecklist, settings.documentTemplates, docStatusFilter]);

  const updateDocField = (name, field, value) => {
    if (viewOnly) return;
    const currentChecklist = student.documentChecklist || {};
    onUpdateDocuments(student.id, {
        ...currentChecklist,
        [name]: {
            ...(currentChecklist[name] || { status: 'Pending', notes: '', shared: false }),
            [field]: value
        }
    });
  };

  const handleToggleChecklistShared = async () => {
    if (viewOnly) return;
    const newStatus = !student.checklistShared;
    const studentId = String(student.id);
    
    await onUpdateStudent({ checklistShared: newStatus }, studentId);
    
    await onAddTimelineEvent(studentId, {
      type: 'status',
      content: newStatus ? 'Document Checklist was shared with student.' : 'Document Checklist shared status was removed.',
      timestamp: new Date().toISOString(),
      user: currentUser?.name || 'Agent'
    });
  };

  const handleToggleSubmittedToUni = async () => {
    if (viewOnly) return;
    const newStatus = !student.submittedToUni;
    const studentId = String(student.id);
    await onUpdateStudent({ submittedToUni: newStatus }, studentId);
    if (newStatus) {
      await onAddTimelineEvent(studentId, {
        type: 'status',
        content: `Application submitted to university for ${student.stage}.`,
        timestamp: new Date().toISOString(),
        user: currentUser?.name || 'Agent'
      });
    }
  };

  const handleToggleI20Received = async () => {
    if (viewOnly) return;
    const newStatus = !student.receivedI20;
    const studentId = String(student.id);
    await onUpdateStudent({ receivedI20: newStatus }, studentId);
    if (newStatus) {
      await onAddTimelineEvent(studentId, {
        type: 'status',
        content: `Official ${student.stage} document received from university.`,
        timestamp: new Date().toISOString(),
        user: currentUser?.name || 'Agent'
      });
    }
  };

  const handleToggleVisaSubmitted = async () => {
    if (viewOnly) return;
    const newStatus = !student.visaAppSubmitted;
    const studentId = String(student.id);
    await onUpdateStudent({ visaAppSubmitted: newStatus }, studentId);
    if (newStatus) {
      await onAddTimelineEvent(studentId, {
        type: 'status',
        content: 'Visa application submitted / Slot booked.',
        timestamp: new Date().toISOString(),
        user: currentUser?.name || 'Agent'
      });
    }
  };

  const handleToggleVisaGranted = async () => {
    if (viewOnly) return;
    const newStatus = !student.visaGranted;
    const studentId = String(student.id);
    await onUpdateStudent({ visaGranted: newStatus }, studentId);
    if (newStatus) {
      await onAddTimelineEvent(studentId, {
        type: 'status',
        content: 'Visa Granted successfully.',
        timestamp: new Date().toISOString(),
        user: currentUser?.name || 'Agent'
      });
    }
  };

  const handleToggleVisaRejected = async () => {
    if (viewOnly) return;
    const newStatus = !student.visaRejected;
    const studentId = String(student.id);
    await onUpdateStudent({ visaRejected: newStatus }, studentId);
    if (newStatus) {
      await onAddTimelineEvent(studentId, {
        type: 'status',
        content: 'Visa application was rejected.',
        timestamp: new Date().toISOString(),
        user: currentUser?.name || 'Agent'
      });
    }
  };

  const handleAddCustomDoc = (e) => {
    if (viewOnly) return;
    e.preventDefault();
    if (!customDocName.trim()) return;
    updateDocField(customDocName.trim(), 'status', 'Pending');
    setCustomDocName('');
  };

  const handleAddNote = (e) => {
    if (viewOnly) return;
    e.preventDefault();
    if (!newNote.trim()) return;
    onAddTimelineEvent(student.id, {
      type: 'note',
      content: newNote,
      timestamp: new Date().toISOString(),
      user: currentUser?.name || 'Agent'
    });
    setNewNote('');
  };

  const handleStageChange = (e) => {
    if (viewOnly) return;
    e.preventDefault();
    if (stageForm.stage === student.stage) {
      setShowStageModal(false);
      return;
    }
    onUpdateStage(student.id, stageForm.stage, stageForm.reason);
    setShowStageModal(false);
    setStageForm({ ...stageForm, reason: '' });
  };

  const timeline = [...(student.timeline || [])].sort((a, b) => 
    new Date(b.timestamp) - new Date(a.timestamp)
  );

  // Helper for Milestone Toggle UI using Brand Colors
  const MilestoneToggle = ({ label, isActive, onToggle, colorHex = '#0464AD' }) => (
    <div className="flex items-center justify-between group py-1">
      <span className="text-[11px] font-bold text-slate-400 group-hover:text-slate-200 transition-colors uppercase tracking-tight">{label}</span>
      <button 
        onClick={viewOnly ? undefined : onToggle}
        disabled={viewOnly}
        className={`w-10 h-5 rounded-full relative transition-all duration-300 shadow-inner ${viewOnly ? 'cursor-not-allowed opacity-60' : ''}`}
        style={{ backgroundColor: isActive ? colorHex : '#334155' }}
      >
        <div className={`absolute top-1 w-3 h-3 bg-white rounded-full shadow-sm transition-all duration-300 ${isActive ? 'left-6' : 'left-1'}`} />
      </button>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500 pb-20">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button onClick={onClose} className="w-10 h-10 bg-white rounded-xl shadow-sm border border-slate-200 flex items-center justify-center text-slate-400 hover:text-[#0464AD] transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
          </button>
          <div>
            <h2 className="text-2xl font-black text-slate-900 tracking-tight leading-none uppercase">{displayName}</h2>
            <div className="flex items-center gap-3 mt-1.5">
                <span className="text-[10px] font-black text-[#0464AD] bg-[#0464AD]/5 px-2 py-0.5 rounded border border-[#0464AD]/10 uppercase tracking-widest">{student.twgProgram}</span>
                <span className="text-slate-300 text-[10px]">/</span>
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-tight">
                  {student.destinationUniversity || 'University TBD'}
                </span>
            </div>
          </div>
        </div>
        {viewOnly ? (
          <div className="bg-amber-50 text-amber-600 px-4 py-2 rounded-xl border border-amber-100 text-[10px] font-black uppercase tracking-widest">
            Read Only
          </div>
        ) : (
          <button 
            onClick={() => onEditProfile(student)} 
            className="px-6 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-black shadow-sm hover:bg-slate-50 transition-all"
          >
            Edit Details
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
        <div className="lg:col-span-2 space-y-6">
            {/* Status & Tabs */}
            <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-6 flex items-center justify-between border-b border-slate-50">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl shadow-inner" style={{ backgroundColor: `${config.color}20`, color: config.color }}>{config.icon}</div>
                        <div>
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Current Stage</p>
                            <h3 className="text-lg font-black text-slate-800 uppercase" style={{ color: config.color }}>{student.stage}</h3>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {!viewOnly && (
                          <div className="flex items-center gap-2">
                            <button onClick={() => setShowStageModal(true)} className="bg-slate-900 text-white px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[#0464AD] transition-all shadow-lg active:scale-95">
                              Change Stage
                            </button>
                          </div>
                        )}
                    </div>
                </div>
                <div className="flex px-6 bg-slate-50/50">
                    <button onClick={() => setActiveTab('timeline')} className={`py-4 px-4 text-[10px] font-black uppercase tracking-widest border-b-2 transition-all ${activeTab === 'timeline' ? 'border-[#0464AD] text-[#0464AD]' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>Timeline Feed</button>
                    <button onClick={() => setActiveTab('documents')} className={`py-4 px-4 text-[10px] font-black uppercase tracking-widest border-b-2 transition-all ${activeTab === 'documents' ? 'border-[#0464AD] text-[#0464AD]' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>Documents & Checklist</button>
                </div>
            </div>

            {/* TAB CONTENT: TIMELINE */}
            {activeTab === 'timeline' && (
                <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-200">
                    {!viewOnly && (
                      <form onSubmit={handleAddNote} className="mb-8 relative">
                        <textarea 
                          className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-[#0464AD] transition-all min-h-[80px] pr-20"
                          placeholder="Quick note..."
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                        />
                        <button type="submit" className="absolute bottom-3 right-3 bg-[#0464AD] text-white px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-[#0091D0] transition-colors shadow-lg">
                          Post Note
                        </button>
                      </form>
                    )}

                    <div className="space-y-4 relative before:absolute before:inset-0 before:left-3 before:w-px before:bg-slate-100">
                        {timeline.map((event, idx) => (
                            <div key={idx} className="relative pl-10">
                                <div className={`absolute left-1.5 top-1.5 w-3 h-3 rounded-full border-2 border-white shadow-sm ${event.type === 'status' ? 'bg-[#0464AD]' : 'bg-slate-400'}`}></div>
                                <div className="flex flex-col gap-1">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                          <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                                            {event.type === 'status' ? 'System' : 'Note'}
                                          </span>
                                          <span className="text-slate-200 text-[8px]">•</span>
                                          <span className="text-[9px] font-bold text-[#0464AD] uppercase">
                                            {event.user || 'Agent'}
                                          </span>
                                        </div>
                                        <span className="text-[9px] font-medium text-slate-400">
                                          {new Date(event.timestamp).toLocaleDateString()} {new Date(event.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                        </span>
                                    </div>
                                    <div className={`p-3 rounded-xl text-xs ${event.type === 'status' ? 'bg-slate-50 text-slate-900 border border-[#DCDDDE]' : 'bg-slate-50/50 text-slate-700 border border-slate-100/50'}`}>
                                        <div className="font-bold">{event.content}</div>
                                        {event.reason && <div className="mt-1 pt-1 border-t border-[#DCDDDE] italic text-[10px] opacity-70">Reason: {event.reason}</div>}
                                    </div>
                                </div>
                            </div>
                        ))}
                        {timeline.length === 0 && <p className="text-center py-10 text-xs text-slate-400 italic">No activity logs found.</p>}
                    </div>
                </div>
            )}

            {activeTab === 'documents' && (
              <div className="space-y-8 animate-in fade-in zoom-in-95 duration-300">
                  
                  {/* SECTION: REQUIREMENT CHECKLIST */}
                  <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
                      <div className="p-6 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex flex-col gap-4">
                              <div className="flex items-center gap-4">
                                  <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest">Requirements: {student.targetCountry || 'No Country Set'}</h3>
                                  <div className="flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-lg border border-[#DCDDDE]">
                                      <input 
                                          type="checkbox" 
                                          id="profileChecklistShared"
                                          checked={student.checklistShared || false} 
                                          onChange={handleToggleChecklistShared}
                                          className="w-4 h-4 rounded border-slate-300 text-[#0464AD] focus:ring-[#0464AD] cursor-pointer"
                                      />
                                      <label htmlFor="profileChecklistShared" className="text-[10px] font-black text-[#0464AD] uppercase tracking-widest cursor-pointer select-none">Checklist Shared</label>
                                  </div>
                              </div>
                              <div className="flex gap-1.5 p-1 bg-slate-100 rounded-lg w-fit">
                                  {['All', ...STATUS_OPTIONS].map(status => (
                                      <button
                                          key={status}
                                          onClick={() => setDocStatusFilter(status)}
                                          className={`px-3 py-1 text-[9px] font-black uppercase tracking-tight rounded-md transition-all ${
                                              docStatusFilter === status 
                                              ? 'bg-white text-[#0464AD] shadow-sm' 
                                              : 'text-slate-400 hover:text-slate-600'
                                          }`}
                                      >
                                          {status}
                                      </button>
                                  ))}
                              </div>
                          </div>
                          <form onSubmit={handleAddCustomDoc} className="flex gap-2">
                              <input type="text" placeholder="Add custom requirement..." className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:ring-2 focus:ring-[#0464AD] min-w-[200px]" value={customDocName} onChange={e => setCustomDocName(e.target.value)} />
                              <button type="submit" className="bg-slate-900 text-white px-4 py-2 rounded-lg text-[10px] font-black uppercase shadow-md hover:bg-[#0464AD] transition-all">+</button>
                          </form>
                      </div>

                      <div className="overflow-x-auto">
                          <table className="w-full text-left border-collapse">
                              <thead className="bg-slate-50/50 border-b border-slate-100">
                                  <tr>
                                      <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Document Name</th>
                                      <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest w-40">Status</th>
                                      <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Notes / Remarks</th>
                                  </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-50">
                                  {checklistItems.length > 0 ? checklistItems.map((item) => (
                                      <tr key={item.id} className="hover:bg-slate-50/30 transition-colors group">
                                          <td className="px-6 py-4">
                                              <div className="flex items-center gap-3">
                                                  <div className={`w-2 h-2 rounded-full flex-shrink-0`} style={{ backgroundColor: item.status === 'Complete' ? '#00B7CF' : '#F15E3B' }}></div>
                                                  <div>
                                                      <p className="font-bold text-slate-800 text-sm leading-tight uppercase">{item.name}</p>
                                                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-tighter mt-0.5">{item.isTemplate ? 'Standard' : 'Custom'}</p>
                                                  </div>
                                              </div>
                                          </td>
                                          <td className="px-6 py-4">
                                              <select 
                                                  value={item.status || 'Pending'} 
                                                  disabled={viewOnly}
                                                  onChange={(e) => updateDocField(item.name, 'status', e.target.value)}
                                                  className={`w-full px-3 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest outline-none border transition-all cursor-pointer ${
                                                      item.status === 'Complete' ? 'bg-emerald-50 text-[#00B7CF] border-[#D1D3D4]' : 
                                                      'bg-red-50 text-[#F15E3B] border-[#D1D3D4]'
                                                  }`}
                                              >
                                                  {STATUS_OPTIONS.map(opt => (
                                                      <option key={opt} value={opt} className="bg-white text-slate-700 font-bold">{opt}</option>
                                                  ))}
                                              </select>
                                          </td>
                                          <td className="px-6 py-4">
                                              <input 
                                                  type="text" 
                                                  placeholder="Enter notes..."
                                                  readOnly={viewOnly} 
                                                  className="w-full bg-slate-50/50 border border-slate-100 rounded-xl px-4 py-2 text-[11px] font-medium text-slate-600 outline-none focus:ring-2 focus:ring-[#0464AD] transition-all italic"
                                                  value={item.notes}
                                                  onChange={e => updateDocField(item.name, 'notes', e.target.value)}
                                              />
                                          </td>
                                      </tr>
                                  )) : (
                                      <tr><td colSpan="3" className="py-20 text-center opacity-30 italic text-sm">No requirements found.</td></tr>
                                  )}
                              </tbody>
                          </table>
                      </div>
                  </div>

                  {/* SECTION: DOCUMENT REPOSITORY */}
                  <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-200">
                      <div className="flex items-center justify-between mb-8">
                          <div>
                              <h3 className="text-xl font-black text-slate-800 uppercase tracking-tighter">Document Repository</h3>
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Click a card below to view the file</p>
                          </div>
                          {!viewOnly && (
                            <label className="bg-slate-900 text-white px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[#0464AD] transition-all cursor-pointer shadow-lg">
                              + Upload New File
                              <input type="file" className="hidden" onChange={handleFileUpload} />
                            </label>
                          )}
                      </div>

                      <div className="space-y-8">
                          {[
                              { label: 'Evaluation & Internal Docs', data: student.evaluationDocuments, icon: '📋' },
                              { label: 'Academic Documents', data: student.academicDocumentsUrls, icon: '🎓' },
                              { label: 'Financial Documents', data: student.relevantFinancialDocsUrls, icon: '💰' },
                              { label: 'English Proficiency', data: student.englishTestResultUrls, icon: '🌐' },
                              { label: 'Personal & Admission (Sec 8)', data: [...(student.section8PersonalDocsUrls || []), ...(student.section8AdmissionDocsUrls || [])], icon: '👤' }
                          ].map((category) => (
                              category.data && category.data.length > 0 && (
                                  <div key={category.label}>
                                      <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                                          <span>{category.icon}</span> {category.label}
                                      </h4>
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                          {category.data.map((doc, idx) => {
                                              // LOGIC FIX: Determine if 'doc' is an object {url, name} or just a string "https://..."
                                              const fileUrl = typeof doc === 'string' ? doc : doc?.url;
                                              const fileName = typeof doc === 'string' ? `Document ${idx + 1}` : (doc?.name || 'View Document');

                                              return (
                                                  <a 
                                                      key={idx} 
                                                      href={fileUrl} 
                                                      target="_blank" 
                                                      rel="noopener noreferrer" 
                                                      className="flex items-center justify-between p-4 bg-slate-50 border border-slate-100 rounded-2xl group hover:border-[#0464AD] hover:bg-white transition-all cursor-pointer shadow-sm hover:shadow-md relative z-10"
                                                      onClick={(e) => {
                                                          if (!fileUrl) {
                                                              e.preventDefault();
                                                              alert("Document URL is missing in the database.");
                                                          }
                                                      }}
                                                  >
                                                      <div className="flex items-center gap-3 overflow-hidden pointer-events-none">
                                                          <div className="w-10 h-10 bg-white rounded-xl flex-shrink-0 flex items-center justify-center shadow-sm text-[#0464AD] group-hover:bg-[#0464AD] group-hover:text-white transition-colors">
                                                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                                              </svg>
                                                          </div>
                                                          <div className="truncate">
                                                              <p className="text-xs font-bold text-slate-800 truncate group-hover:text-[#0464AD] transition-colors">
                                                                  {fileName}
                                                              </p>
                                                              <p className="text-[9px] text-slate-400 font-bold uppercase tracking-tight">
                                                                  {doc?.uploadedAt ? `Uploaded ${new Date(doc.uploadedAt).toLocaleDateString()}` : 'Application File'}
                                                              </p>
                                                          </div>
                                                      </div>
                                                      <div className="text-slate-300 group-hover:text-[#0464AD] transition-colors pointer-events-none">
                                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                          </svg>
                                                      </div>
                                                  </a>
                                              );
                                          })}
                                      </div>
                                  </div>
                              )
                          ))}
                      </div>
                  </div>
              </div>
          )}  

        </div>



        {/* Sidebar */}
        <div className="space-y-6">
            {/* Enrollment Details Summary */}
            <div className="bg-white rounded-[2rem] p-8 border border-slate-200 shadow-sm">
                <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-8">Enrollment Summary</h3>
                <div className="space-y-6">
                    <div>
                        <label className="text-[9px] font-black text-slate-400 uppercase block mb-1">University</label>
                        <p className="text-sm font-bold text-slate-800 uppercase">{student.destinationUniversity || 'TBD'}</p>
                    </div>
                    <div>
                        <label className="text-[9px] font-black text-slate-400 uppercase block mb-1">Target Program</label>
                        <p className="text-sm font-bold text-slate-800 uppercase">{student.targetProgram || 'TBD'}</p>
                    </div>
                    <div>
                        <label className="text-[9px] font-black text-slate-400 uppercase block mb-1">Commencing Term</label>
                        <p className="text-sm font-bold text-slate-800 uppercase">
                          {student.commencingMonth} {student.commencingYear || 'TBD'}
                        </p>
                    </div>
                    <div>
                      <label className="text-[9px] font-black text-slate-400 uppercase block mb-1">Expected Progression</label>
                      <p className="text-sm font-bold text-slate-800 uppercase">
                        {student.expectedProgressionMonth} {student.expectedProgressionYear || 'Not Set'}
                      </p>
                    </div>
                </div>
            </div>

            {/* Contact Info */}
            <div className="bg-slate-900 rounded-[2rem] p-8 text-white shadow-xl shadow-slate-200">
                <h3 className="text-xs font-black text-[#DCDDDE] uppercase tracking-[0.2em] mb-8">Contact Info</h3>
                <div className="space-y-6">
                    <div>
                        <label className="text-[9px] font-black text-slate-500 uppercase block mb-1">Email Address</label>
                        <p className="text-sm font-bold truncate">{student.email}</p>
                    </div>
                    <div>
                        <label className="text-[9px] font-black text-slate-500 uppercase block mb-1">Source Agent</label>
                        <p className="text-sm font-bold truncate uppercase">{student.sourceAgent || 'Direct'}</p>
                    </div>

                    {/* NEW: B2B Team Owner Display */}
                    {b2bOwner && (
                        <div className="pt-4 border-t border-slate-800 animate-in fade-in duration-500">
                            <label className="text-[9px] font-black text-slate-500 uppercase block mb-1">B2B Team Owner</label>
                            <p className="text-sm font-bold truncate uppercase text-blue-100">{b2bOwner.name}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Key Milestones Section */}
            <div className="bg-slate-900 rounded-[2rem] p-8 text-white shadow-xl shadow-slate-200 border border-slate-800">
                <h3 className="text-xs font-black text-[#DCDDDE] uppercase tracking-[0.2em] mb-8">Key Milestones</h3>
                <div className="space-y-6">
                    {/* Documentation Milestones */}
                    <div className="space-y-3">
                        <label className="text-[9px] font-black text-[#00B7CF] uppercase block mb-2 opacity-80 tracking-wider">Documentation</label>
                        <MilestoneToggle 
                            label="Checklist Shared" 
                            isActive={student.checklistShared} 
                            onToggle={handleToggleChecklistShared}
                            colorHex="#00B7CF"
                        />
                    </div>

                    {/* I20 Milestones */}
                    <div className="space-y-3 pt-2 border-t border-slate-800">
                        <label className="text-[9px] font-black text-[#F15E3B] uppercase block mb-2 opacity-80 tracking-wider">I20 / CAS / CoE</label>
                        <MilestoneToggle 
                            label="Submitted to Uni" 
                            isActive={student.submittedToUni} 
                            onToggle={handleToggleSubmittedToUni}
                            colorHex="#F15E3B"
                        />
                        <MilestoneToggle 
                            label="Received I20/CAS/CoE" 
                            isActive={student.receivedI20} 
                            onToggle={handleToggleI20Received}
                            colorHex="#0091D0"
                        />
                    </div>

                    {/* Visa Milestones */}
                    <div className="space-y-3 pt-2 border-t border-slate-800">
                        <label className="text-[9px] font-black text-[#FCB017] uppercase block mb-2 opacity-80 tracking-wider">Visa Process</label>
                        <MilestoneToggle 
                            label="Slot Booked / App Submitted" 
                            isActive={student.visaAppSubmitted} 
                            onToggle={handleToggleVisaSubmitted}
                            colorHex="#FCB017"
                        />
                        <MilestoneToggle 
                            label="Visa Granted" 
                            isActive={student.visaGranted} 
                            onToggle={handleToggleVisaGranted}
                            colorHex="#00B7CF"
                        />
                        <MilestoneToggle 
                            label="Visa Rejected" 
                            isActive={student.visaRejected} 
                            onToggle={handleToggleVisaRejected}
                            colorHex="#606161"
                        />
                    </div>
                </div>
            </div>

            {student.driveLink && (
                <a href={student.driveLink} target="_blank" rel="noopener noreferrer" className="block w-full bg-[#0464AD] text-white p-6 rounded-[2rem] text-center shadow-xl shadow-slate-200 hover:bg-[#0091D0] transition-all active:scale-95">
                    <span className="text-[10px] font-black uppercase tracking-[0.2em] block mb-1 opacity-60">Application Docs</span>
                    <span className="text-xs font-black uppercase">Open Google Drive</span>
                </a>
            )}
        </div>
      </div>

      {/* Stage Change Modal */}
      {showStageModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-[2.5rem] w-full max-w-md shadow-2xl p-8 animate-in zoom-in-95 duration-200">
                <h3 className="text-2xl font-black text-slate-900 tracking-tight mb-2 uppercase">Update Stage</h3>
                <form onSubmit={handleStageChange} className="space-y-6 mt-6">
                    <select 
                        className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD]" 
                        value={stageForm.stage} 
                        onChange={(e) => setStageForm({ ...stageForm, stage: e.target.value })} 
                        required
                    >
                        {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <textarea 
                        className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD] min-h-[120px]" 
                        placeholder="Reason for update..." 
                        value={stageForm.reason} 
                        onChange={(e) => setStageForm({ ...stageForm, reason: e.target.value })} 
                        required 
                    />
                    <div className="flex gap-4">
                        <button type="button" onClick={() => setShowStageModal(false)} className="flex-1 py-4 text-xs font-black text-slate-400 uppercase tracking-widest">Cancel</button>
                        <button type="submit" className="flex-1 bg-[#0464AD] text-white py-4 rounded-2xl text-xs font-black uppercase tracking-widest shadow-xl shadow-slate-200">Confirm</button>
                    </div>
                </form>
            </div>
        </div>
      )}
    </div>
  );
};

export default StudentProfile;